import { useEffect, useMemo, useState } from 'react';
import { Building, Eye, Download, ExternalLink } from 'lucide-react';
import { getPublicResourcePortal, freshPublicResourceFileUrl, isDeskteBlobUrl } from '../api.js';

// Public, no-login client resource portal. Reached at the PATH /resource-portal/:token
// (routed by AuthGate). Fetches { client, resources } from the public backend
// endpoint by token — it does NOT rely on the agent's in-memory data, so a client
// without a Deskte account can open the link (previously it sat behind the login
// wall and rendered a blank/black screen — Lissette 2026-06-29).
export function PublicResourcePortal({ token: tokenProp }) {
  // Back-compat: also accept the legacy #resources/<token> hash form.
  const token = tokenProp || (() => {
    const m = (typeof window !== 'undefined' ? (window.location.hash || '') : '').match(/#resources\/(.+)/);
    return m ? m[1] : null;
  })();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // { client, resources }
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); setError(true); return; }
    let alive = true;
    setLoading(true);
    getPublicResourcePortal(token)
      .then((d) => { if (alive) { setData(d); setLoading(false); } })
      .catch(() => { if (alive) { setError(true); setLoading(false); } });
    return () => { alive = false; };
  }, [token]);

  const client = data?.client || null;
  const resources = data?.resources || [];
  const isEs = client?.preferredLang === "spanish";

  // Get resources shared with this client (backend already filtered, but keep
  // the grouping working on the returned list).
  const clientResources = useMemo(() => resources, [resources]);

  // Group by carrier, then by category
  const grouped = useMemo(() => {
    const map = {};
    clientResources.forEach(r => {
      const carrier = r.carrier || "Other";
      if (!map[carrier]) map[carrier] = {};
      const cat = r.category || "Other";
      if (!map[carrier][cat]) map[carrier][cat] = [];
      map[carrier][cat].push(r);
    });
    return map;
  }, [clientResources]);

  // ABRIR UN FICHERO DEL PORTAL, RE-FIRMANDO PRIMERO.
  //
  // Lissette 2026-09-02, queja 13 (ticket de Gabriel De Silva): "Ver Video
  // redirige a Azure y sale un error XML de autenticacion". La lista se firma
  // UNA vez al cargar la pagina, con dos horas de vida; a partir de ahi cada
  // clic llevaba al cliente a la pagina de error de Azure. Y no hay forma de
  // "reintentar" una navegacion: para cuando Azure contesta, el cliente ya esta
  // mirando el XML. Por eso se firma ANTES de abrir, no despues de fallar --
  // el mismo criterio que BlobLink usa dentro del CRM.
  //
  // Se reserva la pestana con el gesto del clic todavia en la mano: una abierta
  // despues del await es un popup y el navegador la bloquea. No se usa
  // 'noopener' al reservarla porque entonces window.open devuelve null y queda
  // una pestana en blanco; se corta `opener` a mano, que protege igual.
  const abrirRecurso = async (e, res) => {
    const url = res?.fileUrl || res?.fileData || '';
    if (!isDeskteBlobUrl(res?.fileUrl)) return; // externo (YouTube/Drive): que el <a> haga lo suyo
    e.preventDefault();
    const tab = window.open('about:blank', '_blank');
    if (tab) { try { tab.opener = null; } catch { /* cross-origin, ya es seguro */ } }
    const fresca = await freshPublicResourceFileUrl(token, res);
    if (tab) tab.location = fresca || url;
    else window.open(fresca || url, '_blank', 'noopener');
  };

  const getYouTubeId = (url) => { const m = (url||"").match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/); return m?m[1]:null; };
  const getVimeoId = (url) => { const m = (url||"").match(/vimeo\.com\/(\d+)/); return m?m[1]:null; };
  const typeIcon = (type) => ({ video:"🎬", pdf:"📕", image:"🖼️", document:"📄" }[type] || "📄");

  if (loading) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#f8fafc", fontFamily:"'Inter',system-ui,sans-serif", padding:"1rem" }}>
        <div style={{ textAlign:"center", color:"#64748b", fontSize:".9rem" }}>Cargando…</div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#f8fafc", fontFamily:"'Inter',system-ui,sans-serif", padding:"1rem" }}>
        <div style={{ textAlign:"center", maxWidth:400 }}>
          <div style={{ fontSize:"3rem", marginBottom:"1rem" }}>🔗</div>
          <h1 style={{ fontSize:"1.3rem", fontWeight:800, color:"#1e293b", marginBottom:".5rem" }}>
            {isEs ? "Link Inválido o Expirado" : "Invalid or Expired Link"}
          </h1>
          <p style={{ color:"#64748b", fontSize:".9rem" }}>
            {isEs ? "Por favor contacta a tu agente para un nuevo link." : "Please contact your agent for a new link."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:"100vh", background:"#f8fafc", fontFamily:"'Inter',system-ui,sans-serif" }}>
      {/* Header */}
      <div style={{ background:"#fff", borderBottom:"1px solid #e2e8f0", padding:"1rem 1.5rem", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center" }}>
          <img src="/logo/deskte-black.svg" alt="Deskte" style={{ height: 40, width: "auto", display: "block" }}/>
        </div>
        <div style={{ fontSize:".75rem", color:"#94a3b8", fontWeight:600 }}>
          {isEs ? "Portal de Recursos" : "Resource Portal"}
        </div>
      </div>

      <div style={{ maxWidth:900, margin:"0 auto", padding:"2rem 1.25rem" }}>
        {/* Welcome */}
        <div style={{ textAlign:"center", marginBottom:"2rem" }}>
          <h1 style={{ fontSize:"1.5rem", fontWeight:800, color:"#1e293b", marginBottom:".3rem" }}>
            {isEs ? `Hola, ${client.firstName}` : `Hello, ${client.firstName}`} 👋
          </h1>
          <p style={{ color:"#64748b", fontSize:".9rem" }}>
            {isEs ? "Aquí encontrarás los recursos compartidos por tu agente de seguros." : "Here you'll find the resources shared by your insurance agent."}
          </p>
        </div>

        {clientResources.length === 0 ? (
          <div style={{ textAlign:"center", padding:"3rem 1rem", color:"#94a3b8" }}>
            <div style={{ fontSize:"2.5rem", marginBottom:".5rem" }}>📭</div>
            <div style={{ fontWeight:600 }}>{isEs ? "No hay recursos disponibles en este momento." : "No resources available at this time."}</div>
          </div>
        ) : (
          Object.entries(grouped).map(([carrier, categories]) => (
            <div key={carrier} style={{ marginBottom:"2rem" }}>
              <div style={{ display:"flex", alignItems:"center", gap:".5rem", marginBottom:"1rem" }}>
                <Building size={18} style={{ color:"#0ea5e9" }}/>
                <h2 style={{ fontSize:"1.1rem", fontWeight:800, color:"#1e293b", margin:0 }}>{carrier}</h2>
              </div>
              {Object.entries(categories).map(([category, items]) => (
                <div key={category} style={{ marginBottom:"1.25rem", marginLeft:".5rem" }}>
                  <div style={{ fontSize:".8rem", fontWeight:700, color:"#64748b", marginBottom:".5rem", textTransform:"uppercase", letterSpacing:".05em" }}>{category}</div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:".6rem" }}>
                    {items.map(res => (
                      <div key={res.id} style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:12, overflow:"hidden", transition:"box-shadow .15s" }}
                        onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,.08)"}
                        onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
                        {/* Video embed */}
                        {res.type === "video" && getYouTubeId(res.fileUrl) && (
                          <iframe width="100%" height="160" src={`https://www.youtube.com/embed/${getYouTubeId(res.fileUrl)}`} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen/>
                        )}
                        {res.type === "video" && getVimeoId(res.fileUrl) && (
                          <iframe width="100%" height="160" src={`https://player.vimeo.com/video/${getVimeoId(res.fileUrl)}`} frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen/>
                        )}
                        {/* Image preview */}
                        {res.type === "image" && (res.fileUrl || res.fileData) && (
                          <img src={res.fileUrl || res.fileData} alt={res.title} style={{ width:"100%", height:160, objectFit:"cover" }}
                            onError={async (ev) => {
                              // Una firma caducada deja la vista previa rota y en silencio.
                              // Se reintenta UNA vez: si vuelve la misma, no se insiste.
                              const img = ev.currentTarget;
                              if (img.dataset.reintentado || !isDeskteBlobUrl(res.fileUrl)) return;
                              img.dataset.reintentado = "1";
                              const fresca = await freshPublicResourceFileUrl(token, res);
                              if (fresca && fresca !== img.src) img.src = fresca;
                            }}/>
                        )}
                        <div style={{ padding:".75rem" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:".4rem", marginBottom:".3rem" }}>
                            <span style={{ fontSize:"1rem" }}>{typeIcon(res.type)}</span>
                            <div style={{ fontWeight:700, fontSize:".85rem", color:"#1e293b" }}>{res.title}</div>
                          </div>
                          {res.description && <div style={{ fontSize:".75rem", color:"#64748b", lineHeight:1.4, marginBottom:".5rem" }}>{res.description}</div>}
                          {/* Action buttons */}
                          {res.type === "pdf" && (res.fileUrl || res.fileData) && (
                            <a href={res.fileUrl || res.fileData} onClick={(e)=>abrirRecurso(e,res)} target="_blank" rel="noopener noreferrer" style={{ display:"inline-flex", alignItems:"center", gap:4, padding:".35rem .7rem", borderRadius:8, background:"#ef4444", color:"#fff", fontSize:".75rem", fontWeight:700, textDecoration:"none" }}>
                              <Eye size={12}/> {isEs ? "Ver PDF" : "View PDF"}
                            </a>
                          )}
                          {res.type === "document" && (res.fileUrl || res.fileData) && (
                            <a href={res.fileUrl || res.fileData} download={res.fileName} onClick={(e)=>abrirRecurso(e,res)} target="_blank" rel="noopener noreferrer" style={{ display:"inline-flex", alignItems:"center", gap:4, padding:".35rem .7rem", borderRadius:8, background:"#3b82f6", color:"#fff", fontSize:".75rem", fontWeight:700, textDecoration:"none" }}>
                              <Download size={12}/> {isEs ? "Descargar" : "Download"}
                            </a>
                          )}
                          {res.type === "video" && res.fileUrl && !getYouTubeId(res.fileUrl) && !getVimeoId(res.fileUrl) && (
                            <a href={res.fileUrl} onClick={(e)=>abrirRecurso(e,res)} target="_blank" rel="noopener noreferrer" style={{ display:"inline-flex", alignItems:"center", gap:4, padding:".35rem .7rem", borderRadius:8, background:"#8b5cf6", color:"#fff", fontSize:".75rem", fontWeight:700, textDecoration:"none" }}>
                              <ExternalLink size={12}/> {isEs ? "Ver Video" : "Watch Video"}
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}

        {/* Footer */}
        <div style={{ textAlign:"center", marginTop:"3rem", paddingTop:"1.5rem", borderTop:"1px solid #e2e8f0", fontSize:".7rem", color:"#94a3b8" }}>
          Powered by <strong>Deskte</strong> &mdash; Your Insurance Desk
        </div>
      </div>
    </div>
  );
}
